import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useNarrow } from '../../lib/use-narrow';
import { useConversations } from '../../lib/conversations';
import { useBasePath } from '../../lib/use-base-path';
import { useConversationRow } from '../../lib/use-conversation-row';
import { useChat } from '../../lib/use-chat';
import { latestMeetingCard, meetingCardFromHistory, publishMeetingConversation } from '../../lib/meeting-conversation-store';
import { lookupMeetingByConversation } from '../../lib/meetings-channel';
import { useOnboardingEntry } from '../../lib/use-onboarding-entry';
import { useRecipient } from '../settings/shared/settings-queries';
import { useChatsGate } from '../../lib/chats-flag';
import { useActiveConversation } from '../chat/ActiveConversationContext';
import { SymphonyEnsemble } from '../SymphonyEnsemble';
import { ChatFilesPanel } from './chat-surface/ChatFilesPanel';
import { MessagesPanel } from './chat-surface/MessagesPanel';
import { NEW_THREAD_SEED } from './chat-surface/message-utils';
import { externalChannelOf } from '../../lib/external-channel';
import { MEETINGS_CHAT_CHANNEL } from '../../lib/use-chats-filters';
import { useChatReadAcknowledgment } from '../../lib/chat-reads';
import { useConversationNotificationsRead } from '../../lib/conversation-notification-read';
import { queryPath } from '../../lib/query-path';
import { PACK_HANDOFF_PARAM } from '../../lib/pack-handoff';
import { nudgeLiveDocs } from '../../lib/use-live-doc-file';

export { EmptyLandingHero } from './chat-surface/EmptyLandingHero';
export { FourUGrid } from './chat-surface/FourUGrid';

export function ChatSurface({
  variant = 'full',
  // When the (store-driven) Symphony overlay is grown to fill the pane, it
  // renders the full chat presentation — constellation backdrop, empty
  // landing, generous padding — instead of the compact panel. Conversation
  // selection is unaffected: the overlay is always store-driven regardless of
  // size, so toggling this never re-keys useChat (the live stream / voice call
  // ride straight through the resize).
  expanded = false,
  // The side-docked chat (the 370px right column) — as opposed to fullscreen,
  // which shares this same mounted instance. Drives the composer's fixed
  // side-dock size (see MessagesPanel's form).
  sideDocked = false,
  // The per-chat Files/Media panel (#7133 Phase 4) is opened from the chat
  // HEADER (FloatingChat/ChatHeader), which owns the open state; this surface
  // only renders the panel (it owns the messages the panel lists).
  filesOpen = false,
  visible = true,
  onCloseFiles,
}: {
  variant?: 'full' | 'floating';
  expanded?: boolean;
  sideDocked?: boolean;
  filesOpen?: boolean;
  visible?: boolean;
  onCloseFiles?: () => void;
}) {
  const {
    state: conversationsState,
    prepend: prependConversation,
    refresh: refreshConversations,
  } = useConversations(true);
  const router = useRouter();
  // Path-preserving navigation: any router.replace/push needs to keep the
  // user on whichever mount they came in on (/orion-web vs /symphony-web)
  // — hardcoding /orion-web would silently teleport symphony users back
  // to the legacy URL after each send.
  const basePath = useBasePath();
  const { activeConversationId, setActiveConversationId, setChatMode, pendingPrefill, consumePrefill } =
    useActiveConversation();

  // The "full" home surface treats the URL as the single source of truth for
  // which conversation is open — the sidebar (in AppFrame) and this page both
  // derive selection from ?conversation=…, so a Link click updates the URL and
  // the chat reacts. The "floating" widget can't: it rides over other screens
  // (/widgets/<slug>, /tasks, …), so it reads the shared active-conversation store
  // and only SEEDS it from the URL.
  const isFull = variant === 'full';
  // Full presentation (constellation, empty landing, generous padding) when
  // this is the Symphony overlay grown to fill the pane. The compact panel is
  // the floating overlay at rest.
  const fullPresentation = isFull || expanded;
  // `?new=1` starts a fresh thread, overriding any stale conversation in the
  // URL. Honored on both the full surface and the Symphony overlay —
  // chat handoffs (Pulse Edit, Task chat-about, Knowledge Edit) land on the
  // home route. An imperative side-chat handoff (openChatWithMessage) says the
  // same thing via the store: a message with no target thread is a fresh one.
  const isNewConv = pendingPrefill ? pendingPrefill.conversationId === null : router.query.new === '1';
  // One conversation query param across the app — `?conversation=<id>`, never a
  // path segment, so it never changes the route (and so a sidebar switch only
  // swaps the thread, not the page). Full reads it as its selection; the overlay
  // seeds the store from it.
  const urlConversationId =
    !isNewConv && typeof router.query.conversation === 'string' ? router.query.conversation : null;
  const selectedId = isFull ? urlConversationId : activeConversationId;

  // Home (full) reflects its URL selection (incl. "no conversation") into the
  // store so a later mount / the sidebar agree on which conversation is open.
  useEffect(() => {
    if (isFull) setActiveConversationId(selectedId);
  }, [isFull, selectedId, setActiveConversationId]);

  // Overlay seeds the store from the URL once per value (deep-link / navigate /
  // handoff); a fresh-thread handoff (?new=1) clears it once. Afterwards the
  // store is authoritative, so the picker / "+" can switch without this effect
  // yanking the selection back to the URL.
  const seededConvRef = useRef<string | null>(null);
  // The thread `?conversation=` still names while a freshly created one waits for its route
  // update. A STORE handoff (`openChatWithMessage`) leaves the previous thread on the URL — it
  // navigates nowhere — so the instant `consumePrefill` drops `pendingPrefill`, `isNewConv` goes
  // false and `urlConversationId` resolves to that previous id for the render or two before
  // `router.replace` lands. Seeding from it there yanks the chat off the conversation it has just
  // opened, mid-turn.
  const staleUrlConvRef = useRef<string | null>(null);
  useEffect(() => {
    if (isFull) return;
    if (isNewConv) {
      if (seededConvRef.current !== NEW_THREAD_SEED) {
        seededConvRef.current = NEW_THREAD_SEED;
        setActiveConversationId(null);
      }
      return;
    }
    // Ahead of the already-seeded return below, which the new thread's own id satisfies first: the
    // guard would then stay armed for the rest of the session, and a later deep link back to that
    // previous thread could never open it.
    if (urlConversationId && urlConversationId === staleUrlConvRef.current) return;
    staleUrlConvRef.current = null;
    if (!urlConversationId || seededConvRef.current === urlConversationId) return;
    seededConvRef.current = urlConversationId;
    setActiveConversationId(urlConversationId);
  }, [isFull, isNewConv, urlConversationId, setActiveConversationId]);

  // Chat handoffs drop a pre-filled prompt to auto-send once the right thread is
  // active (an existing one fully loaded, or a new one ready). Two sources: the
  // imperative in-place handoff (openChatWithMessage → `pendingPrefill`, cleared
  // via consumePrefill once it fires) and the legacy cross-route link (`prefill`
  // + `autosend=1` in the URL, stripped after send). The store handoff wins.
  // Never on the pack accept landing: it renders inside this floating chat, and an invite that
  // carries a prompt must not auto-send it here, before the pack is accepted, into whatever thread is
  // open. The landing forwards it to the chat its CTA opens instead (see `lib/pack-handoff`).
  const onPackAcceptPage = router.pathname === '/pack-accept';
  const urlPrefill = !onPackAcceptPage && typeof router.query.prefill === 'string' ? router.query.prefill : null;
  const prefill = pendingPrefill?.message ?? urlPrefill;
  // Not gated on `Boolean(urlPrefill)`: a Home "+ → Contact" handoff carries `autosend=1` with an
  // EMPTY `prefill` (the contact token is the only text, appended once useAutosend's own
  // pendingContacts handoff lands) — that transfer clears pendingContacts, so keying this on it
  // would flip shouldAutosend false right as the send becomes ready. useAutosend still bails on a
  // genuinely empty `outgoingPrefill`, so a stray `autosend=1` with nothing to send is a no-op.
  const shouldAutosend = pendingPrefill != null || (!onPackAcceptPage && router.query.autosend === '1');
  // A handoff that is a REPLY to an inbound outreach names it, so the autosend can carry
  // `references.outreachId` and the agent knows to relay the message rather than answer it.
  const handoffOutreachId = typeof router.query.outreach === 'string' ? router.query.outreach : null;
  // The imperative handoff's HIDDEN context line — sent as the stream endpoint's top-level `context`, the
  // same channel the phones use. Store-only on purpose: it never rides the URL (see `ChatHandoffNew.context`).
  const handoffContext = pendingPrefill?.context ?? null;
  // The card a task CTA opened this chat from — rides as `references.taskId`, store-only like the
  // context line above.
  const handoffTaskId = pendingPrefill?.taskId ?? null;
  // Attachments the handoff already uploaded — today only a home-recorded voice note, whose
  // `message` is deliberately EMPTY (see `PendingPrefill.attachments`). Store-only: a URL handoff
  // has nowhere to carry a file.
  const handoffAttachments = pendingPrefill?.attachments ?? null;
  const narrow = useNarrow();
  const onConversationCreated = useCallback(
    (id: string) => {
      // See `staleUrlConvRef`: the URL may still name the previous thread, and dropping the prefill
      // on the next line is what exposes it to the seeding effect.
      const urlConv = typeof router.query.conversation === 'string' ? router.query.conversation : null;
      staleUrlConvRef.current = urlConv && urlConv !== id ? urlConv : null;
      seededConvRef.current = id;
      // An imperative handoff's fresh thread now exists — drop the pending
      // prefill so it can't re-fire, mirroring the URL path's param strip below.
      consumePrefill();
      // Record the new thread as active so the home view and the floating
      // widget agree on which conversation is open.
      setActiveConversationId(id);
      // active=true unblocks the c.active !== false filter so the new entry
      // is visible immediately; the server flips it durably on first persist.
      prependConversation({ id, createdDate: new Date().toISOString(), active: true });
      // Stamp the new thread into the URL as ?conversation=<id> as soon as we
      // have the id, so a refresh resumes it and any handoff params (?new) clear.
      if (isFull) {
        void router.replace(
          { pathname: '/', query: { conversation: id } },
          `${basePath}/?conversation=${encodeURIComponent(id)}`,
          { shallow: true },
        );
        return;
      }
      // The overlay stamps it onto the CURRENT route (shallow) — staying on
      // whatever screen the chat is floating over, not jumping back to `/`.
      const query: Record<string, string | string[] | undefined> = { ...router.query, conversation: id };
      delete query.new;
      delete query.message;
      delete query.prefill;
      delete query.autosend;
      delete query.outreach;
      // The rewrite that was putting the consumed pack marker back in the address bar: the accept lands on
      // `/?chat=fullscreen&packPending=1` and the greeting's own create stamps `?conversation=` onto that
      // same query. See `PACK_HANDOFF_PARAM`.
      delete query[PACK_HANDOFF_PARAM];
      const display = typeof window !== 'undefined' ? window.location.pathname : '';
      void router.replace(
        { pathname: router.pathname, query },
        queryPath(display, query),
        { shallow: true },
      );
    },
    [isFull, setActiveConversationId, prependConversation, router, basePath, consumePrefill],
  );
  // After every turn, refresh the conversation list a few times. The harness
  // fires title generation async (VMR ConsumersImpl, PromptHub job) and can
  // regenerate the title on subsequent turns — so we can't stop early on
  // "any name exists" or we'd miss later updates.
  const onTurnComplete = useCallback(() => {
    const delays = [0, 2000, 5000, 10000];
    let cancelled = false;
    (async () => {
      for (const d of delays) {
        if (d > 0) await new Promise((r) => setTimeout(r, d));
        if (cancelled) return;
        await refreshConversations();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshConversations]);
  // The list is one page of conversations, so the open chat may not be in it (just created, or older than
  // the page). Everything row-scoped on this screen — the recap, the read-only channel, the agent-platform
  // CTA — reads this, so it falls back to fetching the row itself.
  const selectedConversation = useConversationRow(
    selectedId ?? undefined,
    conversationsState.kind === 'ready' && selectedId
      ? conversationsState.conversations.find((c) => c.id === selectedId)
      : undefined,
  );
  const { data: recipient } = useRecipient();
  const { enabled: chatsEnabled } = useChatsGate();
  const [latestVisible, setLatestVisible] = useState(false);
  const chatOwner = recipient?.email && recipient.metaSiteId ? JSON.stringify([recipient.email, recipient.metaSiteId]) : null;
  const chat = useChat(selectedId, onConversationCreated, onTurnComplete, {
    enabled: chatsEnabled && (router.pathname === '/chats' || selectedConversation?.source === 'SCHEDULER'), owner: chatOwner,
    targetMessageId: typeof router.query.message === 'string' ? router.query.message : null,
    visible,
  });
  // Tell the header whether this thread is a meeting's write-up (its badge and time line read the
  // store); the marker rides the rows, and only this surface has them.
  // The marker sits on the thread's FIRST row and the window is bounded, so a thread that grew past it
  // shows no marker; then, and only when older rows exist, the meetings history is asked instead. A
  // marker once published is never cleared by a window that lacks it.
  const conversationId = chat.conversationId;
  const olderCursor = chat.history?.olderCursor ?? null;
  useEffect(() => {
    if (!conversationId) return;
    const card = latestMeetingCard(chat.messages);
    if (card) {
      publishMeetingConversation(conversationId, card);
      return;
    }
    if (!olderCursor) return;
    let cancelled = false;
    void lookupMeetingByConversation(conversationId).then((row) => {
      if (!cancelled && row) publishMeetingConversation(conversationId, meetingCardFromHistory(row));
    });
    return () => {
      cancelled = true;
    };
  }, [conversationId, chat.messages, olderCursor]);
  // Reading the chat clears its bell notifications, whatever route opened it (see the hook). Re-armed
  // when a message lands, not on every streamed token.
  useConversationNotificationsRead(chat.conversationId, visible, chat.streaming ? 'streaming' : String(chat.messages.length));
  // A file the agent rewrites usually comes back as a NEW attachment on the same path ("Done — new version
  // attached"). Every attachment this transcript gains after its first render tells an open copy of that
  // file to re-read (`nudgeLiveDocs`), so the document beside the chat updates in place instead of the
  // reader having to open the new chip. Seeded silently on the first pass and per conversation, so loading
  // a thread's history refreshes nothing.
  const seenAttachmentsRef = useRef<{ conversationId: string | null; keys: Set<string> } | null>(null);
  useEffect(() => {
    const keys = new Set<string>();
    const fresh: string[] = [];
    const seen = seenAttachmentsRef.current;
    const sameThread = seen != null && seen.conversationId === conversationId;
    for (const message of chat.messages) {
      for (const attachment of message.attachments ?? []) {
        if (!attachment.vfsPath) continue;
        const key = `${message.id}:${attachment.vfsPath}`;
        keys.add(key);
        if (sameThread && !seen.keys.has(key)) fresh.push(attachment.vfsPath);
      }
    }
    seenAttachmentsRef.current = { conversationId, keys };
    if (fresh.length > 0) nudgeLiveDocs(fresh);
  }, [chat.messages, conversationId]);
  const readAcknowledgment = useChatReadAcknowledgment(chatOwner, chat.conversationId, chatsEnabled ? chat.history?.readThroughToken ?? null : null,
    chatsEnabled && visible && latestVisible && !!chat.history?.enabled && !chat.loading && !chat.streaming,
    chat.history?.reload);
  // Externally-owned conversations (WhatsApp/SMS threads, and AGENT sessions held through an external
  // agent platform) are read-only on the web: messages render, the composer locks into a "continue …"
  // bar. Locked up front off the sidebar DTO's channel; useChat's typed-403 backstop
  // (readOnlyChannel) covers stale client state where a send still got through.
  const lockedChannel = chat.readOnlyChannel ?? externalChannelOf(selectedConversation?.communicationChannel);
  // EXTERNAL_AGENT only: the platform name + return link for that bar's CTA. Per-conversation (each agent has
  // its own link), unlike WhatsApp/SMS which have one constant destination — and already sanitized
  // server-side, so its presence means there is a safe href to render.
  const lockedAgentPlatform = selectedConversation?.agentPlatform ?? null;

  // Proactive onboarding entry — the assistant SPEAKS FIRST. The decision is DERIVED from the recipient's
  // onboarding state + whether any conversation exists (`useOnboardingEntry` → the native `onboardingRoute`
  // port in lib/onboarding-route.ts), NOT a `?welcome=true` URL param a navigation path could forget (the
  // bug PR #7717 patched). On a genuine first landing (home, nothing selected, `kickoff` route) fire the
  // greeting and go fullscreen. Idempotent by construction — no session flag: the in-mount ref guards the
  // async create window, the created thread flips the route off `kickoff`, and `/api/onboarding/start`
  // itself no-ops for an already-greeted user. The chat is opened by the always-mounted AppFrame trigger
  // (which is what MOUNTS this surface); this effect fires the greeting once it is mounted.
  const { route: onboardingRoute, resolved: onboardingResolved } = useOnboardingEntry();

  // Hold the chat behind a skeleton until the caller's recipient resolves — the chat has no identity to
  // send under and no onboarding decision without it. `useRecipient` returns null both while loading and
  // during the post-signup index lag (it polls), so "have a recipient" is simply a non-null record. The
  // query is shared with AppFrame/useOnboardingEntry, so a returning user's cache is already warm.
  const waitingForRecipient = recipient == null;
  const onboardingKickoffRef = useRef(false);
  useEffect(() => {
    if (onboardingKickoffRef.current) return;
    if (router.pathname !== '/') return;
    if (shouldAutosend || isNewConv) return;
    if (selectedId || chat.conversationId || chat.streaming) return;
    if (!onboardingResolved || onboardingRoute !== 'kickoff') return;
    onboardingKickoffRef.current = true;
    // Full screen, not the side dock: onboarding happens entirely in this conversation, so there is no
    // screen beside it worth keeping in view.
    setChatMode('fullscreen');
    void chat.startOnboarding();
  }, [router.pathname, shouldAutosend, isNewConv, selectedId, chat, onboardingRoute, onboardingResolved, setChatMode]);

  // Focus/typing in the input flips this on so the constellation can react
  // ("the M42 nebula notices you").
  const [inputAwake, setInputAwake] = useState(false);

  return (
    <>
      {/* Full-viewport backdrop — only the full home surface. /symphony-web
          ships the ensemble arc of warm seats. Honors the awake/dim props so the
          empty→conversation transition feels right. The Symphony overlay (even
          maximized) sits on the app's own CosmicBackdrop instead (see
          FloatingChat), so a second ensemble would just double up. */}
      {isFull && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}>
          <SymphonyEnsemble dim={chat.conversationId !== null} awake={inputAwake || chat.streaming} />
        </div>
      )}

      <MessagesPanel
        // The panel's own open/closed state, for the one thing that must not count while the floating
        // chat rests unseen: the survey card's impression.
        surfaceVisible={visible}
        variant={variant}
        expanded={expanded}
        sideDocked={sideDocked}
        chat={chat}
        readAcknowledgment={chatsEnabled ? readAcknowledgment : undefined}
        onLatestVisibilityChange={setLatestVisible}
        onInputAwakeChange={setInputAwake}
        narrow={narrow}
        shouldAutosend={shouldAutosend}
        prefill={prefill}
        handoffOutreachId={handoffOutreachId}
        handoffTaskId={handoffTaskId}
        handoffContext={handoffContext}
        handoffAttachments={handoffAttachments}
        isNewConv={isNewConv}
        selectedId={selectedId}
        prefillFromStore={pendingPrefill != null}
        consumePrefill={consumePrefill}
        lockedChannel={lockedChannel}
        lockedAgentPlatform={lockedAgentPlatform}
        lockedIsWaGroup={Boolean(selectedConversation?.isWaGroup)}
        recap={selectedConversation?.recap}
        waitingForRecipient={waitingForRecipient}
        meetingThread={selectedConversation?.communicationChannel === MEETINGS_CHAT_CHANNEL}
        // A brief is read from its summary card and never honours an open-at-message target — the rule
        // both phones apply from the same `source` (iOS `beginConversationOpen`, Android
        // `openChatTargetRouted`).
        // TRI-STATE: undefined until the row resolves. `=== 'BRIEF'` alone would answer a confident
        // `false` for every brief whose row is still in flight, which is exactly when a deep-linked brief
        // would flash the target its link named.
        briefThread={selectedConversation ? selectedConversation.source === 'BRIEF' : undefined}
        // The empty landing (centered input + 4U grid) is a full-screen layout
        // — the home or the expanded overlay. Never in the small floating
        // panel; there we fall straight to the input-pinned chat even with no
        // messages yet.
        suppressEmptyLanding={!fullPresentation || !router.isReady || shouldAutosend}
        missingMovement={
          // 404 hero only applies to a URL-referenced conversation on the full
          // surface; the floating widget never deep-links a thread. Require BOTH
          // conditions: the conv isn't in the sidebar list AND the messages
          // fetch errored server-side. List-only triggers false-positives
          // during list refreshes and on freshly-created empties that haven't
          // synced back from server.
          isFull &&
          conversationsState.kind === 'ready' &&
          selectedId !== null &&
          !selectedConversation &&
          chat.error !== null
        }
      />

      {/* The Files/Media panel (#7133 Phase 4). Its trigger lives in the chat header
          (ChatHeader), which owns `filesOpen`; this surface only renders the panel. */}
      {filesOpen && (
        <ChatFilesPanel conversationId={chat.conversationId} onClose={() => onCloseFiles?.()} />
      )}
    </>
  );
}
